import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart, ShoppingBag, ShieldCheck, Truck, RotateCcw, Star, Camera, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ProductCard from "@/components/ProductCard";
import TryOnModal from "@/components/tryon/TryOnModal";

export default function ProductDetail() {
  const { id } = useParams();
  const { user, refreshCart, refreshWishlist, wishlist } = useAuth();
  const [p, setP] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [reviews, setReviews] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const [tryOnGlobal, setTryOnGlobal] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/settings/try-on").then(r => setTryOnGlobal(!!r.data.global_enabled)).catch(()=>{});
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/products/${id}`);
      setP(data); setSize(data.sizes?.[0] || ""); setColor(data.colors?.[0] || "");
      const [r, s] = await Promise.all([
        api.get(`/reviews/${id}`),
        api.get(`/products?category=${encodeURIComponent(data.category)}&limit=8`),
      ]);
      setReviews(r.data); setSimilar(s.data.filter(x => x.product_id !== id));
    })();
  }, [id]);

  if (!p) return <div className="min-h-[60vh] grid place-items-center text-slate-500">Loading…</div>;

  const isWished = (wishlist || []).some(w => w.product_id === p.product_id);
  const discount = p.mrp > p.price ? Math.round((1 - p.price / p.mrp) * 100) : 0;

  const addCart = async (buyNow = false) => {
    if (!user) { toast.error("Please login first"); nav("/login"); return; }
    await api.post("/cart/add", { product_id: p.product_id, quantity: 1, size, color });
    await refreshCart();
    if (buyNow) nav("/checkout"); else toast.success("Added to cart");
  };
  const toggleWish = async () => {
    if (!user) { toast.error("Please login first"); return; }
    await api.post("/wishlist/toggle", { product_id: p.product_id });
    await refreshWishlist();
  };
  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Login to review"); return; }
    await api.post("/reviews", { product_id: p.product_id, rating, comment });
    const r = await api.get(`/reviews/${p.product_id}`);
    setReviews(r.data); setComment("");
    toast.success("Review submitted");
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="grid grid-cols-[80px_1fr] gap-3">
          <div className="flex flex-col gap-2">
            {(p.images || []).map((im, i) => (
              <button key={i} onClick={() => setImgIdx(i)} className={`aspect-square rounded-xl overflow-hidden border-2 ${i === imgIdx ? "border-[#004BCA]" : "border-transparent"}`}>
                <img src={im} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-slate-100 relative">
            <img src={p.images?.[imgIdx]} alt={p.title} className="zoom-img w-full h-full object-cover" />
            {discount > 0 && <span className="absolute top-4 left-4 bg-[#FF4500] text-white font-bold text-sm px-3 py-1 rounded-full">{discount}% OFF</span>}
          </div>
        </div>

        <div>
          {/* 📷 TRY IT ON — placed below gallery, above product title/name */}
          {tryOnGlobal && p.try_on_enabled && (
            <button
              data-testid="tryon-open-btn"
              onClick={() => setTryOnOpen(true)}
              className="w-full sm:w-auto mb-5 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-sm bg-gradient-to-r from-[#004BCA] to-[#FF4500] text-white shadow-lg hover:scale-[1.02] transition-transform"
            >
              <Camera size={16}/> TRY IT ON
              <Sparkles size={12} className="text-[#FFC85C]"/>
            </button>
          )}
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{p.brand}</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">{p.title}</h1>
          <div className="flex items-center gap-3 mt-3">
            <span className="bg-emerald-600 text-white text-xs px-2 py-0.5 rounded inline-flex items-center gap-1">{p.rating?.toFixed?.(1) || p.rating} <Star size={11} className="fill-white"/></span>
            <span className="text-sm text-slate-500">{reviews.length} reviews</span>
          </div>
          <div className="flex items-baseline gap-3 mt-5">
            <span className="font-display text-3xl font-bold text-[#004BCA]">{inr(p.price)}</span>
            {p.mrp > p.price && <span className="text-base line-through text-slate-400">{inr(p.mrp)}</span>}
            {discount > 0 && <span className="text-sm font-semibold text-emerald-600">{discount}% off</span>}
          </div>
          <p className="text-slate-600 mt-5 leading-relaxed">{p.description}</p>

          {p.sizes?.length > 0 && (
            <div className="mt-6">
              <div className="text-sm font-semibold mb-2">Select Size</div>
              <div className="flex flex-wrap gap-2">
                {p.sizes.map(s => (
                  <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)} className={`min-w-[44px] h-11 px-3 rounded-full border text-sm font-medium ${size === s ? "border-[#004BCA] bg-[#004BCA] text-white" : "border-slate-200 hover:border-slate-400"}`}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {p.colors?.length > 0 && (
            <div className="mt-5">
              <div className="text-sm font-semibold mb-2">Color: <span className="text-slate-500 font-normal">{color}</span></div>
              <div className="flex flex-wrap gap-2">
                {p.colors.map(c => (
                  <button key={c} data-testid={`color-${c}`} onClick={() => setColor(c)} className={`px-4 h-10 rounded-full border text-sm ${color === c ? "border-[#004BCA] bg-[#004BCA]/5" : "border-slate-200 hover:border-slate-400"}`}>{c}</button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-8">
            <button data-testid="add-to-cart-pdp" onClick={() => addCart(false)} className="btn-primary-sf inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold flex-1">
              <ShoppingBag size={18}/> Add to Cart
            </button>
            <button data-testid="buy-now-pdp" onClick={() => addCart(true)} className="btn-fire inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold flex-1">
              Buy Now
            </button>
            <button data-testid="wish-pdp" onClick={toggleWish} className="w-12 h-12 grid place-items-center rounded-full border border-slate-200 hover:border-slate-400">
              <Heart size={20} className={isWished ? "fill-[#FF4500] text-[#FF4500]" : ""}/>
            </button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3 text-xs">
            <Trust icon={Truck} t="Free Shipping" />
            <Trust icon={RotateCcw} t="7-Day Returns" />
            <Trust icon={ShieldCheck} t="Secure Payments" />
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-16">
        <h2 className="font-display text-2xl font-bold mb-4">Ratings & Reviews</h2>
        <form onSubmit={submitReview} className="bg-slate-50 rounded-2xl p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            {[1,2,3,4,5].map(n => (
              <button type="button" key={n} onClick={()=>setRating(n)}>
                <Star size={22} className={n<=rating?"fill-[#FF4500] text-[#FF4500]":"text-slate-300"} />
              </button>
            ))}
          </div>
          <textarea data-testid="review-text" value={comment} onChange={e=>setComment(e.target.value)} placeholder="Share your experience…" className="w-full bg-white rounded-xl p-3 border border-slate-200 text-sm" rows={3} />
          <button data-testid="submit-review" className="mt-3 btn-primary-sf px-5 py-2 rounded-full text-sm font-semibold">Submit Review</button>
        </form>
        {reviews.length === 0 ? <div className="text-sm text-slate-500">No reviews yet. Be the first!</div> : (
          <div className="space-y-4">
            {reviews.map(r => (
              <div key={r.review_id} className="border border-slate-100 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-emerald-600 text-white text-xs px-2 py-0.5 rounded">{r.rating} ★</span>
                  <span className="text-sm font-semibold">{r.user_name}</span>
                </div>
                <p className="text-sm text-slate-700">{r.comment}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Similar */}
      {similar.length > 0 && (
        <section className="mt-16">
          <h2 className="font-display text-2xl font-bold mb-4">You may also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {similar.slice(0, 4).map(sp => <ProductCard key={sp.product_id} p={sp}/>)}
          </div>
        </section>
      )}

      {/* Virtual Try-On modal */}
      <TryOnModal open={tryOnOpen} onClose={() => setTryOnOpen(false)} initialProduct={p}/>
    </main>
  );
}

function Trust({ icon: Icon, t }) {
  return <div className="flex items-center gap-2 p-3 rounded-xl border border-slate-100"><Icon size={16} className="text-[#004BCA]"/><span className="font-medium">{t}</span></div>;
}
